/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _ from 'lodash';

import Localized from '../../../../../shared/components/Localized';
import Service from './Service.js';
import { StatusIcon } from '../../../../components/SVGIcon';

import styles from '../../../../styles';

const Wrapper = styled.div``;
const ServiceRow = styled.div`
  display: flex;
  flex-wrap: wrap;

  :not(first-child) {
    padding-top: 1rem;
  }
`;

const Bar = styled.div`
  letter-spacing: 1px;
  display: flex;
  height: 4rem;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  color: white;
  background-color: ${(p) => p.backgroundColor};
  text-align: left;
  border-radius: 0.25rem;
  padding: 1rem;
`;

const MoveLeft = styled.span`
  margin-left: auto;
  font-size: 14px;
`;

const ServiceStatus = ({ instance }) => {
  const statusMapping = {
    OK: { color: styles.colors.ssGreen, text: <Localized modelName='statusKey' modelAttribute='label' modelKey='available' />, 'icon': 'healthy'},
    MAJOR_INCIDENT_CORE: { color: styles.colors.ssRed, text: <Localized modelName='statusKey' modelAttribute='label' modelKey='disruption' />, 'icon': 'major'},
    MINOR_INCIDENT_CORE: { color: styles.colors.ssOrange, text: <Localized modelName='statusKey' modelAttribute='label' modelKey='degradation' />, 'icon': 'minor'},
    MAINTENANCE_CORE: { color: styles.colors.ssPurple, text: <Localized modelName='statusKey' modelAttribute='label' modelKey='maintenance' />, 'icon': 'maintenance'},
    MAJOR_INCIDENT_NONCORE: { color: styles.colors.ssGreen, text: <Localized modelName='statusKey' modelAttribute='label' modelKey='availablePartialImpact' />, 'icon': 'healthy'},
    MINOR_INCIDENT_NONCORE: { color: styles.colors.ssGreen, text: <Localized modelName='statusKey' modelAttribute='label' modelKey='availablePartialImpact' />, 'icon': 'healthy'},
    MAINTENANCE_NONCORE: { color: styles.colors.ssGreen, text: <Localized modelName='statusKey' modelAttribute='label' modelKey='availablePartialImpact' />, 'icon': 'healthy'},
  };

  const statusObject = statusMapping[instance.status] || statusMapping.OK;
  const serviceList = instance.Services.sort((service1, service2) => service1.order - service2.order);

  return (
    <Wrapper>
      <Bar backgroundColor={ statusObject.color }>
        <Localized modelName='serviceStatus' modelAttribute='label' modelKey='overallSystem' />
        <MoveLeft>{ statusObject.text }</MoveLeft>
        <StatusIcon size='2rem' severity={ statusObject.icon }/>
      </Bar>
      {_.chunk(serviceList, 4).map((serviceRow, i) => (
        <ServiceRow key={i}>
          {serviceRow.map((service, ii) => (
            <Service service={service} key={ii} instance={instance} />
          ))}
        </ServiceRow>
      ))}
    </Wrapper>
  );
}

ServiceStatus.propTypes = {
  instance: PropTypes.shape({
    key: PropTypes.string,
    Incidents: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      serviceKeys: PropTypes.arrayOf(PropTypes.string),
      IncidentImpacts: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        startTime: PropTypes.object,
        endTime: PropTypes.object
      })),
    })),
    Maintenances: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      serviceKeys: PropTypes.arrayOf(PropTypes.string),
      MaintenanceImpacts: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        startTime: PropTypes.object,
        endTime: PropTypes.object
      })),
    }))
  })
};

export default ServiceStatus;
